import React from 'react';
import { Icon, IconSize } from '@askporter/component-library/Icon';
import { LinkedListItem } from '@askporter/component-library/LinkedListItem';
import { OptionsDropdown } from '@askporter/component-library/OptionsDropdown';
import { Typography } from '@askporter/component-library/Typography';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import { SearchTaskResultExternal, TaskTypeReadExternal } from '@askporter/client-grieg-lyric';
import { LinkedTaskMenuOptions, OptionType } from '@askporter/utils';
interface LinkedTasksSectionProps {
  tasks?: SearchTaskResultExternal['results'];
  generateMenuOptions: (
    taskType: TaskTypeReadExternal,
    taskUid: string,
    taskURN: string,
  ) => OptionType<LinkedTaskMenuOptions, SxProps>[];
  menuTitle: string;
  isSmallDevice: boolean;
  /**
   * optional, defaults to 0. Renders this many trailing skeleton placeholder rows, e.g. while newly-linked
   * tasks are still being fetched
   */
  skeletonCount?: number;
}
/**
 * Renders a LinkedTasksSection component
 * @param tasks - tasks to list
 * @param generateMenuOptions - generates menu options
 * @param menuTitle - title for the menu, only seen on mobile
 * @param isSmallDevice - small device or not
 * @param skeletonCount - number of trailing skeleton placeholder rows to render
 */
export const LinkedTasksSection: React.FC<React.PropsWithChildren<LinkedTasksSectionProps>> = ({
  tasks,
  generateMenuOptions,
  menuTitle,
  isSmallDevice,
  skeletonCount = 0,
}: LinkedTasksSectionProps) => {
  const totalCount = (tasks?.length ?? 0) + skeletonCount;
  return (
    <>
      {tasks?.map((task, idx) => {
        const { uid, urn, title, taskRef, taskType } = task || {};
        const options: OptionType<LinkedTaskMenuOptions, SxProps>[] = generateMenuOptions(taskType, uid, urn);
        const icon = task?.taskType?.icon;
        const isLastRow = idx === totalCount - 1;
        return (
          <LinkedListItem
            key={`${uid}-${idx}`}
            title={title}
            titleVariant="h3"
            titleHref={`/app/tasks/${uid}`}
            subTitle={taskRef}
            subTitleBelowTitle
            icon={
              <Icon
                fullPath={icon?.iconPath}
                size={IconSize.MD}
                colorMask={{ size: '48px', color: icon?.iconColourMask }}
              />
            }
            isSmallDevice={isSmallDevice}
            divider={!isLastRow}
          >
            {
              <OptionsDropdown
                isSmallDevice={isSmallDevice}
                optionsParams={{
                  title: menuTitle,
                  options,
                }}
                elementBelowTitle={
                  <Box pl={8} display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" mb={2}>
                    <Icon
                      fullPath={icon?.iconPath}
                      size={IconSize.SM}
                      colorMask={{ size: '32px', color: icon?.iconColourMask }}
                      folder="system/01-tasks"
                    />
                    <Typography ml={2} variant="caption">
                      {title}
                    </Typography>
                    <Typography ml={2} variant="caption">
                      {title && taskRef ? '•' : ''}
                    </Typography>
                    <Typography ml={2} variant="body2" color="textSecondary">
                      {taskRef}
                    </Typography>
                  </Box>
                }
              />
            }
          </LinkedListItem>
        );
      })}
      {Array.from({ length: skeletonCount }, (_, i) => (
        <LinkedListItem
          key={`skeleton-${i}`}
          title=""
          isLoading
          isSmallDevice={isSmallDevice}
          divider={(tasks?.length ?? 0) + i !== totalCount - 1}
        />
      ))}
    </>
  );
};
